import { ChevronRight, Play } from 'lucide-react';
import { useState, useEffect } from 'react';

import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VideoLoader } from '@/components/ui/video-loader';
import { useTranslations } from '@/hooks/use-translations';
import { cn } from '@/lib/utils';

import slidesData from '../data/slides.json';
import { useCarouselVideo } from '../hooks/useCarouselVideo';
import { OnboardingCarouselProps } from '../types';

/**
 * OnboardingCarousel Component
 *
 * Interactive carousel for onboarding new users to the masterclass platform.
 * Displays a series of slides with videos, titles, and descriptions.
 *
 * Features:
 * - Auto-plays videos for each slide
 * - Navigation dots indicator
 * - Skip functionality
 * - Responsive design
 * - Automatic video pause/play on slide change
 *
 * @example
 * ```tsx
 * <OnboardingCarousel
 *   isOpen={showOnboarding}
 *   onClose={() => setShowOnboarding(false)}
 * />
 * ```
 */

export const OnboardingCarousel = ({ isOpen, onClose }: OnboardingCarouselProps) => {
  const { t } = useTranslations();
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [playingVideos, setPlayingVideos] = useState<Set<number>>(new Set());

  const { videoRefs, getVideoRef, isVideoLoading, handleVideoLoaded } = useCarouselVideo(
    slidesData.length,
    isOpen,
    current
  );

  const handlePlayVideo = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      video.play().catch((err) => console.log('Play error:', err));
      setPlayingVideos((prev) => new Set(prev).add(index));
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const handleNext = () => {
    if (current === slidesData.length - 1) {
      onClose();
    } else {
      carouselApi?.scrollNext();
    }
  };

  // Update current slide when carousel changes
  useEffect(() => {
    if (!carouselApi) return;

    setCurrent(carouselApi.selectedScrollSnap());

    carouselApi.on('select', () => {
      setCurrent(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-4xl animate-scale-in overflow-hidden border-border bg-card">
        <DialogTitle className="sr-only">{t('onboarding.carousel.title')}</DialogTitle>
        <DialogDescription className="sr-only">
          {t('onboarding.carousel.description')}
        </DialogDescription>
        <div className="flex flex-col overflow-hidden">
          <Carousel setApi={setCarouselApi} className="w-full flex-1">
            <CarouselContent className="-ml-0">
              {slidesData.map((slide, index) => (
                <CarouselItem key={index} className="pl-0">
                  <div className="space-y-4 p-6 pb-2 md:space-y-6 md:p-8">
                    {/* Video */}
                    <div className="relative flex aspect-video items-center justify-center overflow-hidden rounded-lg bg-white">
                      {isVideoLoading(index) && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center">
                          <VideoLoader size="large" />
                        </div>
                      )}
                      {!playingVideos.has(index) && !isVideoLoading(index) && (
                        <button
                          onClick={() => handlePlayVideo(index)}
                          className="group absolute inset-0 z-20 flex items-center justify-center bg-black/20 transition-colors hover:bg-black/30"
                          aria-label="Play video"
                        >
                          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 transition-transform group-hover:scale-110 md:h-20 md:w-20">
                            <Play
                              className="ml-1 h-8 w-8 text-accent md:h-10 md:w-10"
                              fill="currentColor"
                            />
                          </div>
                        </button>
                      )}
                      <video
                        ref={getVideoRef(index)}
                        src={slide.videoUrl}
                        className={cn(
                          'h-full w-full object-cover transition-opacity duration-500 ease-in-out',
                          isVideoLoading(index) ? 'opacity-0' : 'opacity-100'
                        )}
                        onLoadedData={() => handleVideoLoaded(index)}
                        onPlay={() => setPlayingVideos((prev) => new Set(prev).add(index))}
                        onPause={() =>
                          setPlayingVideos((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(index);
                            return newSet;
                          })
                        }
                        playsInline
                      />
                    </div>

                    {/* Content */}
                    <div className="space-y-2 text-center md:space-y-3">
                      <h2 className="text-xl font-bold text-card-foreground md:text-2xl lg:text-3xl">
                        {slide.title}
                      </h2>
                      <p className="mx-auto max-w-2xl text-sm text-muted-foreground md:text-base lg:text-lg">
                        {slide.message}
                      </p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {/* Navigation - Fixed outside carousel */}
          <div className="flex items-center justify-between border-t border-border px-6 py-4 md:px-8">
            {/* Skip button */}
            <Button
              onClick={handleSkip}
              variant="ghost"
              className="text-sm text-muted-foreground hover:text-card-foreground md:text-base"
            >
              {t('onboarding.carousel.skip')}
            </Button>

            {/* Dots indicator */}
            <div className="flex items-center gap-2">
              {slidesData.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => carouselApi?.scrollTo(idx)}
                  className={`h-2 rounded-full transition-all ${
                    idx === current ? 'w-8 bg-accent' : 'w-2 bg-muted-foreground/30'
                  }`}
                />
              ))}
            </div>

            {/* Next/Start button */}
            <Button
              onClick={handleNext}
              className="gap-2 bg-accent text-sm text-accent-foreground hover:bg-accent/90 md:text-base"
            >
              {current === slidesData.length - 1
                ? t('onboarding.carousel.start')
                : t('onboarding.carousel.next')}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
